import { Section } from "./blocks/demo";
import { useNavigate } from "react-router-dom";
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn } from "react-icons/fa";

/* --- Shared Content --- */
const JOB_SUITE_FEATURES = [
  {
    id: 1,
    title: "AI Resume Scoring",
    desc: "Optimize your resume with AI-driven scoring and feedback. Generate tailored bullet points, quantify impact, and export ATS-friendly versions.",
    img: require("../img/cat-resume.png"),
    color: "bg-purple-200",
  },
  {
    id: 2,
    title: "AI Job Matching",
    desc: "Get personalized job recommendations with fit scores and why-you-match explanations.",
    img: require("../img/cat-jobmatching.png"),
    color: "bg-yellow-200",
  },
  {
    id: 3,
    title: "Career Guidance",
    desc: "Receive roadmaps with stages, courses, and weekly milestones aligned to your target role.",
    img: require("../img/cat-career.png"),
    color: "bg-purple-200",
  },

];

const STUDY_SUITE_FEATURES = [
  {
    id: 1,
    title: "AI Course & University Suggestion",
    desc: "Get tailored programs by country, budget, background, and goals with clear rationale and competitiveness scores.",
    img: require("../img/cat-career.png"),
    color: "bg-purple-200",
  },
  {
    id: 2,
    title: "AI Visa & University Mock Interviews",
    desc: "Practice realistic interviews (speech/text) and receive feedback on intent, finances, ties, and clarity.",
    img: require("../img/cat-laptop.png"),
    color: "bg-yellow-200",
  },
  {
    id: 3,
    title: "SOP / LOR / CV / Cover Builder",
    desc: "Generate, iterate, and polish documents with prompts, structure checks, plagiarism-safe rewrites, and export.",
    img: require("../img/cat-coverletter.png"),
    color: "bg-purple-200",
  }
];

const FAQ_ITEMS = [
  {
    question: "What is the Aspirant Suite?",
    answer:
      "An AI-powered platform for early-career professionals covering resumes, interviews, matching, and career planning.",
  },
  {
    question: "How does AI Resume Scoring work?",
    answer:
      "It benchmarks your resume against the job description and industry patterns, then returns a score with actionable edits.",
  },
  {
    question: "Is the interview practice realistic?",
    answer:
      "Yes—text and speech modes with feedback on structure, content, pace, and filler words.",
  },
  {
    question: "How are jobs recommended?",
    answer:
      "Our recommender matches your skills, history, and interests to roles, highlighting gaps and quick wins.",
  },
  {
    question: "Who is StudyStep for?",
    answer:
      "For students and professionals applying abroad who want AI help with programs, documents, and interviews.",
  },
  {
    question: "How does AI pick courses and universities?",
    answer:
      "It uses your academics, budget, preferences, and goals to rank options with an explainable score.",
  },
  {
    question: "Are visa/university mocks realistic?",
    answer:
      "Yes—speech and text simulations with feedback on intent, ties, finances, and answer structure.",
  },
  {
    question: "Will my SOP/LOR be unique?",
    answer:
      "We guide structure and language while preserving your voice, and run originality and tone checks.",
  },
  {
    question: "Can I track deadlines and intakes?",
    answer:
      "The tracker monitors deadlines, fees, and documents. You can also set reminders per university.",
  },
];

export default function LandingPage() {

  const navigate = useNavigate();


  return (
    <div className="w-full overflow-x-hidden bg-black text-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-black/70 backdrop-blur border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img
              src={require("../img/rakfort.jpeg")}
              alt="RAKFORT"
              className="w-14 h-14 rounded-lg object-cover"
            />
            <span className="font-bold tracking-wide text-lg">NoviStep</span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-sm">
            <a
              onClick={() => navigate("/jobsuite")}
              className="hover:text-yellow-400 cursor-pointer"
            >
              JobStep
            </a>
            <a
              onClick={() => navigate("/studysuite")}
              className="hover:text-yellow-400 cursor-pointer"
            >
              StudyStep
            </a>
            <a href="#faq" className="hover:text-yellow-400">
              FAQs
            </a>
          </div>
        </div>
      </nav>

      {/* Hero - 3D Robot */}
      <Section />

      {/* Intro */}
      <section className="bg-gray-100 text-black py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 items-center">
          <img
            src={require("../img/cat-learning.png")}
            alt="Mascot"
            className="w-52 mb-5 animate-bounce"
            style={{ animationDuration: "2.5s" }}
          />
          <div>
            <h2 className="text-3xl font-bold mb-4">
              From Learning to Earning – AI That Powers Every Aspirant
            </h2>
            <p className="text-gray-700 mb-4">
              Our platform bridges the gap between education, skills, and career
              opportunities through intelligent AI solutions.
            </p>
            <p className="text-gray-700 mb-4">
              We combine AI-guided study tools, job-matching systems, and personal
              career co-pilots into one cohesive platform — designed to support
              every stage of your journey.
            </p>
            <p className="text-gray-700">
              Learn, build, and achieve goals using smart, data-driven technology.
            </p>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="product" className="bg-[#20002c] text-white py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-4xl font-bold text-center mb-12">Products</h2>

          {/* Job Suite */}


          {/* Job Suite Core Features */}
          <div className="cursor-pointer bg-white text-black rounded-3xl p-8 md:p-12 mt-6 shadow-2xl border border-purple-100">
            <h4 onClick={() => navigate("/jobsuite")} className="text-2xl font-semibold text-center mb-4">JobStep — AI Core Features</h4>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-10">
              Built for modern jobseekers and teams. Each feature is backed by explainable AI and delivers
              tangible career outcomes.
            </p>
            <div className="cursor-pointer grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {JOB_SUITE_FEATURES.map((feature) => (
                <div onClick={() => navigate("/jobsuite")}

                  key={feature.id}
                  className={`p-6 rounded-2xl shadow-md text-center cursor-pointer ${feature.color}`}
                >
                  <img src={feature.img} alt={feature.title} className="w-24 mx-auto mb-4" />
                  <h5 className="font-semibold text-lg mb-2 text-gray-900">{feature.title}</h5>
                  <p className="text-sm text-gray-700">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Study Suite */}

          {/* Study Suite Core Features */}
          <div className="bg-white cursor-pointer text-black rounded-3xl p-8 md:p-12 mt-6 shadow-2xl border border-purple-100">
            <h4 onClick={() => navigate("/studysuite")} className="text-2xl font-semibold text-center mb-4">StudyStep — AI Core Features</h4>
            <p className="text-center text-gray-600 max-w-3xl mx-auto mb-10">
              Built for international applicants. Every feature is guided by transparent reasoning to improve outcomes.
            </p>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto cursor-pointer">
              {STUDY_SUITE_FEATURES.map((feature) => (
                <div
                  onClick={() => navigate("/studysuite")}

                  key={feature.id}
                  className={`p-6 rounded-2xl shadow-md text-center cursor-pointer ${feature.color}`}
                >
                  <img src={feature.img} alt={feature.title} className="w-24 mx-auto mb-4" />
                  <h5 className="font-semibold text-lg mb-2 text-gray-900">{feature.title}</h5>
                  <p className="text-sm text-gray-700">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Empower */}
      <section className="bg-[#18001f] text-white py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">
            How Our Platform Empowers Every Aspirant
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Businesses Hiring",
                desc: "Match instantly with qualified candidates & streamline hiring.",
              },
              {
                title: "Educational Institutions",
                desc: "Promote courses, manage enrollments & analyze engagement.",
              },
              {
                title: "Trainers & Mentors",
                desc: "Host sessions, upload courses & monetize your expertise.",
              },
            ].map((c) => (
              <div
                key={c.title}
                className="bg-[#2b003c] rounded-3xl p-8 border border-white/10 shadow-lg"
              >
                <h3 className="text-xl font-semibold text-yellow-400 mb-3">
                  {c.title}
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-gray-100 text-black py-20">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 items-start">
          <img
            src={require("../img/fq.png")}
            alt="FAQ"
            className="w-full rounded-xl"
          />

          <div>
            <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {FAQ_ITEMS.map((item, i) => (
                <details
                  key={i}
                  className="rounded-md border border-gray-300 bg-white p-4"
                >
                  <summary className="font-medium cursor-pointer select-none">
                    {item.question}
                  </summary>
                  <p className="mt-2 text-sm text-gray-600">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="text-white">
        {/* Main Footer Section */}
        <div className="bg-gradient-to-b from-[#2b0b55] to-[#0a0216] py-12 px-8 lg:px-16">
          <div className="flex flex-wrap justify-between gap-12 max-w-7xl mx-auto">
            {/* Left Section - Logo and About */}
            <div className="w-full sm:w-1/4">
              <img
                src={require("../img/rakfort.jpeg")}
                alt="Rakfort Logo"
                className="w-8 h-8 object-cover rounded"
              />
              <p className="text-sm text-gray-300 mb-4 max-w-xs">
                We’re NoviStep — a team driven by insight and connection. Our mission is simple: to bring the right people together, create meaningful collaborations, and help ideas grow into impactful results.
              </p>

              <a href="mailto:info@novistep.com" className="block text-gray-300 hover:text-white mb-4">
                info@novistep.com
              </a>

              {/* Social Icons */}
              <div className="flex space-x-4 mt-4">
                <a href="https://www.linkedin.com/company/rakfort/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500">
                  <FaLinkedinIn />
                </a>
                <a href="https://www.linkedin.com/company/rakfort/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500">
                  <FaFacebookF />
                </a>
                <a href="https://www.linkedin.com/company/rakfort/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500">
                  <FaTwitter />
                </a>
                <a href="https://www.linkedin.com/company/rakfort/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500">
                  <FaInstagram />
                </a>
              </div>
            </div>

            {/* Product Section */}
            <div>
              <h3 className="font-semibold mb-3">Product</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="/jobsuite/interview-prep" className="hover:text-white">AI Interview Prep</a></li>
                <li><a href="/studysuite/resume-builder" className="hover:text-white">AI tools</a></li>
                <li><a href="/jobsuite/resume-scoring" className="hover:text-white">Resume Scoring</a></li>
                <li><a href="/jobsuite/cover-letter-generator" className="hover:text-white">Cover Letter Generator</a></li>
                <li><a href="/jobsuite/job-matching" className="hover:text-white">Job Matching</a></li>
                <li><a href="/jobsuite/resume-builder" className="hover:text-white">Resume Builder</a></li>
              </ul>
            </div>

            {/* Legal Section */}
            <div>
              <h3 className="font-semibold mb-3">Legal</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="/termsconditions" className="hover:text-white">Terms & Conditions</a></li>
                <li><a href="/privacypolicy" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="/cancellationrefund" className="hover:text-white">Cancellation-Refund</a></li>
              </ul>
            </div>

            {/* Explore Section */}
            <div>
              <h3 className="font-semibold mb-3">Explore</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="/studysuite/blogs" className="hover:text-white">Blogs</a></li>
                <li><a href="/contactus" className="hover:text-white">Contact us</a></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="border-t border-gray-800 bg-[#fafafa]">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 py-6">
            <p className="text-center text-sm text-[#3C0F6C]/70">
              © 2025 NoviStep. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}